import React, { useEffect, useState } from 'react'

import {
    AVAILABLE_LANGUAGES,
    DEFAULT_LANGUAGE_CODE,
} from '../../consts'
import i18n from '../../locales/i18n'
import store from '../../store'
import { SET_LOCALE_LANG } from '../../reducers/locale'
import ConnectedRoutes from './ConnectedRoutes'
import {
    auth,
    fetchLoggedUser,
} from '../../components/shared/auth'

/**
 * Returns the active language code if the URL contains a supported
 * language prefix, otherwise redirects to the default language and
 * returns null so the caller can short-circuit rendering.
 *
 * Handles: `/cs/...` (active), `/cs` → redirect to `/cs/`,
 * `/something-else` → redirect to `/{default}/something-else`.
 */
const resolveLanguage = (): string | null => {
    const pathname = window.location.pathname
    const langMatch = pathname.match(/^\/([a-z]{2})(\/.*)?$/i)
    const matchedLang = langMatch?.[1]?.toLowerCase() ?? null

    if (matchedLang && (AVAILABLE_LANGUAGES as readonly string[]).includes(matchedLang)) {
        if (pathname === `/${matchedLang}`) {
            window.location.replace(`/${matchedLang}/${window.location.search}${window.location.hash}`)
            return null
        }
        return matchedLang
    }

    const rest = pathname === '/' ? '/' : pathname
    window.location.replace(`/${DEFAULT_LANGUAGE_CODE}${rest}${window.location.search}${window.location.hash}`)
    return null
}

/**
 * Language switching root component.
 *
 * Determines the active language from the URL, normalises the URL to
 * include a supported language prefix when missing, then renders the
 * connected route tree scoped to the resolved locale.
 */
const LangRouter = () => {
    const [language, setLanguage] = useState<string | null>(null)

    useEffect(() => {
        const resolvedLanguage = resolveLanguage()

        if (!resolvedLanguage) {
            return
        }

        i18n.changeLanguage(resolvedLanguage)

        store.dispatch({
            type: SET_LOCALE_LANG,
            lang: resolvedLanguage,
        })

        auth().onAuthStateChanged(() => {
            fetchLoggedUser()
        })

        setLanguage(resolvedLanguage)
    }, [])

    if (!language) {
        return null
    }

    return <ConnectedRoutes locale={`/${language}`} />
}

export default LangRouter
